import ControlFactory from '@/Factories/ControlFactory';
import ComponentManager from '@/Management/ComponentManager';
import * as NumericTypeHelper from '@/Utility/NumericTypeHelper';
import NumericTextBoxViewModel from '@/ViewModels/Controls/NumericTextBoxViewModel';
import NumericTextBox from '@/Controls/NumericTextBox';
import LabelViewModel from '@/ViewModels/Controls/LabelViewModel';
import { FilterInfo } from '#/DefaultViewExtensionInterfaces';

export default class NumericUpDownFactory extends ControlFactory {

    private jQueryControl: JQuery;
    private NumericTextBoxViewModel = ComponentManager.getViewModel<typeof NumericTextBoxViewModel>('NumericTextBoxViewModel');
    private NumericTextBox = ComponentManager.getControl<typeof NumericTextBox>('NumericTextBox');
    protected LabelViewModel = ComponentManager.getViewModel<typeof LabelViewModel>('LabelViewModel');

    /**
     * @override
     */
    public viewModel: NumericTextBoxViewModel;

    public min: number;
    public max: number;
    public increment: number;

    public constructor(private params: any) {
        super(params);
        this.min = this.paramOrDefault(this.params.min, null);
        this.max = this.paramOrDefault(this.params.max, null);
        this.increment = this.paramOrDefault(this.params.increment, null);
    }

    /**
     * @override
     */
    control: NumericTextBox;

    /**
     * @override
     */
    public getControl(): JQuery {
        if (this.jQueryControl != null) {
            return this.jQueryControl;
        }

        this.viewModel = this.createViewModel();

        this.control = new this.NumericTextBox(this.viewModel);

        let tuple = this.control.getTuple();
        this.jQueryControl = tuple.control;

        this.addOverwritableToControl(this.jQueryControl, [this.detailViewItem.name]);
        this.registerDefaultValidation([this.detailViewItem.name]);
        this.registerTypeValidation(this.detailViewItem.name, (value: any, dotNetTypeName: string): boolean => {
            if (dotNetTypeName != null) {
                let isNullable = dotNetTypeName.indexOf('Nullable') > -1 || this.detailViewItem.useInFilter; // Im Filter muss kein Wert eingetragen werden
                if (value == null && !isNullable) {
                    return false;
                }

                let type = dotNetTypeName;
                if (type != null) {
                    let info = NumericTypeHelper.getDotNetTypeInfo(type);
                    if (info != null) {
                        let min = this.viewModel.min() != null ? Math.max(this.viewModel.min(), info.min) : info.min;
                        let max = this.viewModel.max() != null ? Math.min(this.viewModel.max(), info.max) : info.max;

                        if (value < min || value > max) {
                            return false;
                        }
                    }
                }
            }

            return true;
        });

        return this.jQueryControl;
    }

    public getValueForFilter(): Object {
        let ret = {};
        ret[this.detailViewItem.name] = this.context.genericObject.getValue(this.detailViewItem.name);

        return ret;
    }

    public getFilterInfo(): FilterInfo {
        return {
            field: this.detailViewItem.name,
            value: this.viewModel?.value() || ''
        }
    }

    private createViewModel(): NumericTextBoxViewModel{
        let length = this.detailViewItem.length;
        if (isNaN(length) || length === 0) {
            if (this.detailViewItem.dotNetTypeName == null || this.detailViewItem.dotNetTypeName.indexOf('Double') === -1) {
                length = 255;
            } else {
                length = 326;
            }
        }

        return new this.NumericTextBoxViewModel({
            factory: this,
            isRequired: this.isRequired,
            isEnabled: this.isEnabled,
            isValid: this.isValid,
            isFocusable: this.isFocusable,
            invalidationText: this.invalidationText,
            min: this.min,
            max: this.max,
            step: this.increment,
            floatingPrecision: 0,
            maxLength: length,
            value: this.context.genericObject.getObservable(this.detailViewItem.name),
            showSpinners: true,
            label: new this.LabelViewModel({
                isVisible: !!this.detailViewItem.unitErgName,
                ergName: this.detailViewItem.unitErgName
            }),
            parentLocator: this.locator,
            locator: this.detailViewItem.name,
            tooltip: new this.LabelViewModel({
                ergName: this.detailViewItem.tooltip
            }),
            technicalName: this.detailViewItem.name,
            dotNetTypeName: this.detailViewItem.dotNetTypeName
        })
    }
}

ComponentManager.setFactory('NumericUpDown', NumericUpDownFactory, 'RADPresentation.HTMLClient.DefaultViewExtensions');